import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  Tabs,
  Tab,
  Box,
  Paper,
  Typography,
  Container,
  Zoom,
  Fade,
  Badge,
} from "@mui/material";
import Camera from "../features/cameras/Camera";
import { useDispatch, useSelector } from "react-redux";
import { getCameras, selectCameras } from "../features/cameras/cameraSlice";
import {
  fetchAlarmDevices,
  selectAlarmDevices,
} from "../components/Alarm/alarmSlice";
import {
  fetchSensorStats,
  selectSensorStats,
} from "../features/sensors/sensorSlice"; // Import sensor actions and selectors
import MiniMap from "./MiniMap";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import AlarmManagement from "./AlarmManagement";
import WeatherStations from "./WeatherStations";

export default function Cameras() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0); // 0: Cameras, 1: Alarms, 2: Weather Stations
  const [cameraTab, setCameraTab] = useState(0); // 0: All, 1: Live, 2: Offline

  useEffect(() => {
    dispatch(getCameras());
    dispatch(fetchAlarmDevices());
    dispatch(fetchSensorStats()); // Fetch sensor stats on mount
  }, [dispatch]);

  const { results: cameras } = useSelector(selectCameras);
  const alarmDevices = useSelector(selectAlarmDevices);
  const sensorStats = useSelector(selectSensorStats); // Get sensor stats from Redux

  let location = { lat: 33.734457, lng: 73.045045 };
  const [center, setCenter] = useState({ center: location, zoom: 5 });
  const [filteredCameras, setFilteredCameras] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    live: 0,
    offline: 0,
  });

  const [alarmStats, setAlarmStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  // Define applyCameraFilter with useCallback
  const applyCameraFilter = useCallback(
    (tabValue) => {
      if (!cameras || cameras.length === 0) return;

      let filtered = cameras;

      if (tabValue === 1) {
        // Live
        filtered = cameras.filter((camera) => camera.live === true);
      } else if (tabValue === 2) {
        // Offline
        filtered = cameras.filter((camera) => camera.live === false);
      }

      setFilteredCameras(filtered);
    },
    [cameras],
  );

  useEffect(() => {
    if (cameras && cameras.length > 0) {
      const liveCount = cameras.filter((c) => c.live === true).length;
      const offlineCount = cameras.filter((c) => c.live === false).length;

      setStats({
        total: cameras.length,
        live: liveCount,
        offline: offlineCount,
      });

      // Apply current filter
      applyCameraFilter(cameraTab);
    }
  }, [cameras, cameraTab, applyCameraFilter]);

  // Update alarm stats when alarmDevices change
  useEffect(() => {
    if (alarmDevices?.results) {
      const active = alarmDevices.results.filter(
        (d) => d.status === "ACTIVE",
      ).length;
      const inactive = alarmDevices.results.filter(
        (d) => d.status === "INACTIVE",
      ).length;

      setAlarmStats({
        total: alarmDevices.count || alarmDevices.results.length,
        active,
        inactive,
      });
    }
  }, [alarmDevices]);

  const handleMainTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCameraTabChange = (event, newValue) => {
    setCameraTab(newValue);
  };

  const updateMapCenter = (lat, lng) => {
    let location = { lat: lat, lng: lng };
    setCenter({ center: location, zoom: 12 });
  };

  const renderCamerasContent = () => {
    return (
      <Box sx={{ mt: 1 }}>
        {/* Camera Filter Tabs */}
        <Paper
          sx={{
            borderRadius: "50px",
            overflow: "hidden",
            mb: 1,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <Tabs
            value={cameraTab}
            onChange={handleCameraTabChange}
            // variant="fullWidth"
            sx={{
              bgcolor: "#f8f9fa",
              "& .MuiTab-root": {
                py: 2,
                transition: "all 0.3s ease",
              },
              "& .Mui-selected": {
                bgcolor: "#454545",
                color: "white !important",
                fontWeight: 700,
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab
              icon={<CameraAltIcon sx={{ fontSize: "1.2rem" }} />}
              label={`ALL (${stats.total})`}
              iconPosition="start"
              sx={{
                color: cameraTab === 0 ? "white" : "#666",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            />
            <Tab
              icon={<VideocamIcon sx={{ fontSize: "1.2rem" }} />}
              label={`LIVE (${stats.live})`}
              iconPosition="start"
              sx={{
                color: cameraTab === 1 ? "white" : "#666",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            />
            <Tab
              icon={<VideocamOffIcon sx={{ fontSize: "1.2rem" }} />}
              label={`OFFLINE (${stats.offline})`}
              iconPosition="start"
              sx={{
                color: cameraTab === 2 ? "white" : "#666",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            />
          </Tabs>
        </Paper>

        {/* Camera Grid */}
        <Grid container spacing={3}>
          {filteredCameras.length === 0 ? (
            <Grid item xs={12}>
              <Fade in={true}>
                <Paper
                  sx={{
                    p: 4,
                    textAlign: "center",
                    bgcolor: "#f8f9fa",
                    borderRadius: "20px",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
                    {cameraTab === 1
                      ? "No live cameras available"
                      : cameraTab === 2
                        ? "No offline cameras"
                        : "No cameras available"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    {cameraTab === 1
                      ? "All cameras are currently offline"
                      : cameraTab === 2
                        ? "All cameras are currently online"
                        : "Check back later"}
                  </Typography>
                </Paper>
              </Fade>
            </Grid>
          ) : (
            <>
              {filteredCameras.map((camera, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={camera.id || index}
                >
                  <Zoom
                    in={true}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <Box>
                      <Camera
                        content={camera}
                        latestEvent={
                          camera.latest_event
                            ? `${camera.latest_event}`
                            : "https://storage.googleapis.com/proudcity/mebanenc/uploads/2021/03/placeholder-image-300x225.png"
                        }
                        updateMapCenter={updateMapCenter}
                      />
                    </Box>
                  </Zoom>
                </Grid>
              ))}

              {/* Map - Always show map */}
              <Grid item xs={12} md={5} key="map">
                <Fade in={true}>
                  <Paper
                    elevation={3}
                    sx={{
                      width: "100%",
                      height: "270px",
                      marginTop: "10px",
                      marginLeft: { xs: "0px", md: "18px" },
                      borderRadius: "25px",
                      overflow: "hidden",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    <MiniMap camera={filteredCameras} defaultCenter={center} />
                  </Paper>
                </Fade>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <div className="page">
      <div className="page__content">
        <div className="main-wrapper">
          <Container maxWidth="xl" sx={{ mt: 1, mb: 2 }}>
            {/* Main Header with Tabs */}
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mb: 1,
                backgroundColor: "white",
                borderRadius: "50px",
                boxShadow: "0 2px 20px rgba(0,0,0,0.05)",
                overflow: "hidden",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleMainTabChange}
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    py: 2.5,
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                    fontWeight: 600,
                    transition: "all 0.3s ease",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#454545",
                    color: "white !important",
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                <Tab
                  icon={<CameraAltIcon sx={{ mr: 1 }} />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Cameras
                      <Badge
                        badgeContent={stats.total}
                        color="error"
                        sx={{ ml: 3 }}
                      />
                    </Box>
                  }
                />
                <Tab
                  icon={<NotificationsActiveIcon sx={{ mr: 1 }} />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Alarms
                      <Badge
                        badgeContent={alarmStats.total}
                        color="error"
                        sx={{ ml: 3 }}
                      />
                    </Box>
                  }
                />
                <Tab
                  icon={<WbSunnyIcon sx={{ mr: 1 }} />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Weather Stations
                      <Badge
                        badgeContent={sensorStats.total}
                        color="error"
                        sx={{ ml: 3 }}
                      />
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Content based on active tab */}
            <Fade in={true} timeout={500}>
              <Box>
                {activeTab === 0 && renderCamerasContent()}
                {activeTab === 1 && <AlarmManagement />}
                {activeTab === 2 && <WeatherStations />}
              </Box>
            </Fade>
          </Container>
        </div>
      </div>
    </div>
  );
}
